import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, ChevronLeft } from "lucide-react";
import { PRODUCTS, INDUSTRIES } from "../data/products";
import { ProductHero } from "../components/ProductHero";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "stats", label: "Impact" },
  { id: "capabilities", label: "Capabilities" },
  { id: "usecases", label: "Use cases" },
  { id: "industries", label: "Industry fit" },
  { id: "integrations", label: "Integrations" },
  { id: "faq", label: "FAQ" },
];

export default function PlatformDetail() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const [active, setActive] = useState("overview");
  const sectionRefs = useRef({});

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY + 180;
      let next = "overview";
      for (const t of TABS) {
        const el = sectionRefs.current[t.id];
        if (el && el.offsetTop <= y) next = t.id;
      }
      setActive(next);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!product) return <Navigate to="/platforms" replace />;

  const idx = PRODUCTS.findIndex((p) => p.slug === slug);
  const next = PRODUCTS[(idx + 1) % PRODUCTS.length];
  const siblings = PRODUCTS.filter((p) => p.cat === product.cat && p.slug !== product.slug);

  const jumpTo = (id) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    const nav = 72, tabs = 60;
    window.scrollTo({ top: el.offsetTop - (nav + tabs) - 12, behavior: "smooth" });
  };

  return (
    <article data-testid={`platform-detail-${slug}`}>
      {/* ========== Hero ========== */}
      <section className="pd-hero">
        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          <div className="pd-breadcrumbs">
            <Link to="/">Home</Link> · <Link to="/platforms">Platforms</Link> · <span>Category {product.cat} · {product.catLabel}</span> · <span>{product.indo}</span>
          </div>
          <div className="pd-hero-grid">
            <div>
              <div className="mono" style={{ fontSize: 11, letterSpacing: ".22em", color: "var(--steel)", textTransform: "uppercase", marginBottom: 6 }}>
                {product.cat === "A" ? "A" : "B"}{idx + 1} · {product.catLabel}
              </div>
              <div className="pd-indo" data-testid="pd-indo">
                {product.indo}
                <span className="pd-dev">{product.devnagari}</span>
              </div>
              {product.english && (
                <div className="pd-english" data-testid="pd-english">
                  <span className="pd-english-label">aka</span> {product.english}
                </div>
              )}
              <div className="pd-ipa">/{product.ipa}/ — {product.meaning}</div>
              <div className="pd-name" data-testid="pd-name">{product.name}</div>
              <div className="pd-tag">{product.tagline}</div>
              <div className="pd-desc">{product.desc}</div>
              <div className="pd-hero-ctas">
                <Link to={`/contact?p=${product.slug}`} className="btn btn-primary" data-testid="pd-cta-demo">
                  Request a walkthrough <span className="arrow">→</span>
                </Link>
                <button className="btn btn-ghost" onClick={() => jumpTo("capabilities")} type="button">
                  See capabilities <span className="arrow">→</span>
                </button>
              </div>
            </div>
            <div className="pd-hero-meta" data-testid="pd-hero-meta">
              <div className="pd-meta-title">At a glance</div>
              <div className="pd-meta-row"><span>Category</span><span className="pd-meta-val">{product.cat}</span></div>
              <div className="pd-meta-row"><span>Stage</span><span className="pd-meta-val">{product.meta.stage}</span></div>
              <div className="pd-meta-row"><span>Deployment</span><span className="pd-meta-val">{product.meta.deployment}</span></div>
              <div className="pd-meta-row"><span>Typical duration</span><span className="pd-meta-val">{product.meta.typicalDuration}</span></div>
              <div className="pd-meta-row"><span>Value driver</span><span className="pd-meta-val">{product.meta.valueDriver}</span></div>
              <div className="pd-meta-row"><span>Industries</span><span className="pd-meta-val">{product.industries.length} / 7</span></div>
            </div>
          </div>

          {/* Animated product-specific visual */}
          <div style={{ marginTop: 48 }}>
            <div className="product-hero-frame" data-testid="pd-hero-viz-frame">
              <div className="product-hero-frame-label">
                <span className="mono" style={{ fontSize: 10, letterSpacing: ".2em", color: "var(--text-3)" }}>HERO · {product.heroViz.toUpperCase()}</span>
                <span className="mono" style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--text-3)" }}>{product.heroGraphicDesc}</span>
              </div>
              <ProductHero viz={product.heroViz} />
            </div>
          </div>
        </div>
      </section>

      {/* ========== Sticky tabs ========== */}
      <div className="pd-tabs" data-testid="pd-tabs">
        <div className="container">
          <div className="pd-tabs-inner">
            {TABS.map((t) => (
              <button key={t.id} className={"pd-tab " + (active === t.id ? "active" : "")} onClick={() => jumpTo(t.id)} data-testid={`pd-tab-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ========== Overview ========== */}
      <section className="pd-section" id="overview" ref={(el) => (sectionRefs.current.overview = el)}>
        <div className="container">
          <div className="section-label"><span className="dot" />Overview</div>
          <h2>Why {product.indo}</h2>
          <p className="lede">{product.desc}</p>

          {/* Meaning card row (inlined) */}
          <div className="pd-meaning-grid" style={{ marginTop: 40 }}>
            <div className="pd-meaning-card">
              <div className="pd-meaning-label">Devanagari</div>
              <div className="pd-meaning-devnagari">{product.devnagari}</div>
            </div>
            <div className="pd-meaning-card">
              <div className="pd-meaning-label">Romanisation</div>
              <div className="pd-meaning-indo">{product.indo}</div>
              <div className="pd-meaning-ipa">/{product.ipa}/</div>
            </div>
            <div className="pd-meaning-card">
              <div className="pd-meaning-label">Meaning</div>
              <div className="pd-meaning-text">{product.meaning}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== Stats / Impact ========== */}
      {product.stats && (
        <section className="pd-section pd-stats-band" id="stats" ref={(el) => (sectionRefs.current.stats = el)}>
          <div className="container">
            <div className="section-label"><span className="dot" />Impact</div>
            <h2>The numbers behind {product.indo}.</h2>
            <div className="pd-stats-grid" data-testid="pd-stats-grid">
              {product.stats.map((s, i) => (
                <div className="pd-stat" key={i} data-testid={`pd-stat-${i}`}>
                  <div className="pd-stat-value">
                    <CountUp end={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} />
                  </div>
                  <div className="pd-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== Capabilities ========== */}
      <section className="pd-section" id="capabilities" ref={(el) => (sectionRefs.current.capabilities = el)}>
        <div className="container">
          <div className="section-label"><span className="dot" />Capabilities</div>
          <h2>What it does, end-to-end.</h2>
          <div className="pd-cap-grid">
            {product.features.map((f, i) => (
              <div className="pd-cap" key={i} data-testid={`pd-feature-${i}`}>
                <div className="pd-cap-num">{String(i + 1).padStart(2, "0")}</div>
                <div className="pd-cap-title">{f.title}</div>
                <div className="pd-cap-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== Use cases ========== */}
      <section className="pd-section" id="usecases" ref={(el) => (sectionRefs.current.usecases = el)} style={{ background: "var(--bg-sunk)" }}>
        <div className="container">
          <div className="section-label"><span className="dot" />Use cases</div>
          <h2>Who uses it — and what they get back.</h2>
          <div className="pd-usecases">
            {product.useCases.map((u, i) => (
              <div className="pd-usecase" key={i} data-testid={`pd-usecase-${i}`}>
                <div className="pd-usecase-role">{u.role}</div>
                <div className="pd-usecase-title">{u.title}</div>
                <div className="pd-usecase-outcome">→ {u.outcome}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== Industry fit ========== */}
      <section className="pd-section" id="industries" ref={(el) => (sectionRefs.current.industries = el)}>
        <div className="container">
          <div className="section-label"><span className="dot" />Industry fit</div>
          <h2>{product.industries.length === 7 ? "Deployable across every capital-project industry we serve." : `Purpose-built for ${product.industries.length} of our 7 industries.`}</h2>
          <div className="pd-industry-grid">
            {INDUSTRIES.map((ind) => {
              const fit = product.industries.includes(ind.key);
              return (
                <div key={ind.key} className={"pd-industry-cell " + (fit ? "fit" : "nofit")} data-testid={`pd-industry-${ind.key}`}>
                  <div className="pd-industry-check">{fit ? <Check size={16} /> : "—"}</div>
                  <div>
                    <div className="pd-industry-name">{ind.name}{ind.flagship && <span className="pd-industry-flag">FLAGSHIP</span>}</div>
                    <div className="pd-industry-segs">{ind.segs.slice(0, 3).join(" · ")}{ind.segs.length > 3 ? " · …" : ""}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== Integrations ========== */}
      <section className="pd-section" id="integrations" ref={(el) => (sectionRefs.current.integrations = el)}>
        <div className="container">
          <div className="section-label"><span className="dot" />Integrations</div>
          <h2>Fits into your stack.</h2>
          <p className="lede">Native bi-directional connectors for the systems your teams already run.</p>
          <div className="pd-int-grid">
            {product.integrations.map((intg, idx) => (
              <div className="pd-int" key={idx} data-testid={`pd-integration-${idx}`}>{intg}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== Screens ========== */}
      <section className="pd-section" style={{ background: "var(--bg-sunk)" }}>
        <div className="container">
          <div className="section-label"><span className="dot" />Product screens</div>
          <h2>What you'll actually be looking at.</h2>
          <div className="pd-screens">
            {product.screens.map((s, i) => (
              <div className="pd-screen" key={i} data-testid={`pd-screen-${i}`}>
                <div className="pd-screen-grid" />
                <div style={{ position: "relative", zIndex: 2 }}>
                  <div className="pd-screen-label">{s.label}</div>
                  <div className="pd-screen-title">{s.title}</div>
                </div>
                <MockScreenViz variant={i === 0 ? "dashboard" : "module"} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      {product.faqs && (
        <section className="pd-section" id="faq" ref={(el) => (sectionRefs.current.faq = el)}>
          <div className="container">
            <div className="section-label"><span className="dot" />FAQ</div>
            <h2>Frequently asked about {product.indo}.</h2>
            <div className="pd-faq-list" data-testid="pd-faq-list">
              {product.faqs.map((f, i) => (
                <FaqItem key={i} q={f.q} a={f.a} idx={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== Siblings ========== */}
      <section className="pd-section" style={{ background: "var(--bg-sunk)" }}>
        <div className="container">
          <div className="section-label"><span className="dot" />Within Category {product.cat}</div>
          <h2>{product.catLabel} · the rest of the suite.</h2>
          <div className="pd-siblings">
            {siblings.map((s) => (
              <Link to={`/platforms/${s.slug}`} key={s.slug} className="pd-sibling" data-testid={`pd-sibling-${s.slug}`}>
                <div className="pd-sibling-indo">{s.indo}</div>
                <div className="pd-sibling-name">{s.name}</div>
                <div className="pd-sibling-cta mono">Open <span className="arrow">→</span></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ========== Gradient CTA ========== */}
      <section className="pd-foot-cta">
        <div className="container">
          <div className="pd-foot-cta-inner">
            <div>
              <h3>See {product.indo} against your live project.</h3>
              <div style={{ color: "rgba(243,244,246,0.65)", marginTop: 8, fontSize: 14, fontFamily: "var(--f-mono)", letterSpacing: ".05em" }}>
                Thirty minutes. Your architect, our architect, one walkthrough.
              </div>
            </div>
            <Link to={`/contact?p=${product.slug}`} className="btn btn-primary" data-testid="pd-foot-cta">
              Book a walkthrough <span className="arrow">→</span>
            </Link>
          </div>

          <div style={{ marginTop: 48, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20 }}>
            <Link to="/platforms" style={{ fontFamily: "var(--f-mono)", fontSize: 12, letterSpacing: ".15em", color: "var(--text-3)" }}>← All platforms</Link>
            <Link to={`/platforms/${next.slug}`} style={{ fontFamily: "var(--f-mono)", fontSize: 12, letterSpacing: ".15em", color: "var(--steel)", display: "inline-flex", alignItems: "center", gap: 8 }} data-testid="pd-next-product">
              Next: {next.indo} — {next.name} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}

/* ───── CountUp ───── */
function CountUp({ end, prefix = "", suffix = "", duration = 1400 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(end * eased);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  const display = Number.isInteger(end) ? Math.round(val).toLocaleString("en-IN") : val.toFixed(1);
  return <span ref={ref}>{prefix}{display}{suffix}</span>;
}

/* ───── FAQ accordion item ───── */
function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={"pd-faq " + (open ? "open" : "")} data-testid={`pd-faq-${idx}`}>
      <button className="pd-faq-q" onClick={() => setOpen(!open)} type="button" data-testid={`pd-faq-toggle-${idx}`}>
        <span>{q}</span>
        <ChevronDown size={18} className={"pd-faq-chevron " + (open ? "rotated" : "")} />
      </button>
      {open && <div className="pd-faq-a" data-testid={`pd-faq-answer-${idx}`}>{a}</div>}
    </div>
  );
}

function MockScreenViz({ variant }) {
  if (variant === "dashboard") {
    return (
      <svg viewBox="0 0 640 400" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ position: "absolute", inset: 0, zIndex: 1, padding: 60 }}>
        <rect x="20" y="20" width="260" height="120" rx="8" fill="rgba(46,112,173,0.2)" stroke="rgba(46,112,173,0.5)" />
        <rect x="300" y="20" width="140" height="58" rx="6" fill="rgba(242,107,31,0.2)" stroke="rgba(242,107,31,0.5)" />
        <rect x="460" y="20" width="140" height="58" rx="6" fill="rgba(46,112,173,0.15)" stroke="rgba(46,112,173,0.4)" />
        <rect x="300" y="88" width="300" height="52" rx="6" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.1)" />
        <rect x="20" y="160" width="580" height="220" rx="8" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" />
        {[0,1,2,3,4,5,6,7].map(i => <rect key={i} x={40 + i * 70} y={180 + (i % 3) * 30} width="54" height={70 - (i % 3) * 18} fill="#2E70AD" opacity={0.4 + (i % 3) * 0.2} />)}
        <path d="M 40 310 Q 160 260 280 290 T 600 260" stroke="#F26B1F" strokeWidth="2" fill="none" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 400 400" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ position: "absolute", inset: 0, zIndex: 1, padding: 60 }}>
      <circle cx="200" cy="200" r="80" stroke="#2E70AD" strokeWidth="14" fill="none" opacity="0.3" />
      <circle cx="200" cy="200" r="80" stroke="#F26B1F" strokeWidth="14" fill="none" strokeDasharray="280 502" transform="rotate(-90 200 200)" strokeLinecap="round" />
      <text x="200" y="210" textAnchor="middle" fill="#F3F4F6" fontFamily="JetBrains Mono" fontSize="26" fontWeight="700">68%</text>
    </svg>
  );
}
